import { useState, useEffect, useRef, useContext, CSSProperties } from 'react'
import { StoreContext } from '@store/Root.store'
import './CountdownTimer.css'

interface CountdownTimerProps {
  countdownSeconds: number
  onComplete: () => void
  trans: {
    timerLine?: {
      line1: string
      line2: string
    }
    timerLine1?: string
    timerLine2?: string
  }
}

const CountdownTimer = ({ countdownSeconds, onComplete, trans }: CountdownTimerProps) => {
  const store = useContext(StoreContext)
  const initialTime = countdownSeconds
  const [timeLeft, setTimeLeft] = useState(initialTime)
  const { isMobile } = store.AppState

  const timerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined)

  const startTimer = (finalTimestamp: number) => {
    timerRef.current = setInterval(() => {
      const now = Date.now()
      const remainingTime = Math.max(0, Math.floor((finalTimestamp - now) / 1000))

      if (remainingTime <= 0) {
        clearInterval(timerRef.current)
        sessionStorage.removeItem('finalTimestamp')
        if (onComplete) onComplete()
        setTimeLeft(0)
      } else {
        setTimeLeft(remainingTime)
      }
    }, 1000)
  }

  useEffect(() => {
    const savedFinalTimestamp = sessionStorage.getItem('finalTimestamp')

    if (savedFinalTimestamp) {
      const finalTimestamp = Number(savedFinalTimestamp)
      const now = Date.now()
      const remainingTime = Math.max(0, Math.floor((finalTimestamp - now) / 1000))

      if (remainingTime <= 0) {
        setTimeLeft(0)
        sessionStorage.removeItem('finalTimestamp')
        if (onComplete) onComplete()
      } else {
        setTimeLeft(remainingTime)
        startTimer(finalTimestamp)
      }
    } else {
      const finalTimestamp = Date.now() + initialTime * 1000
      sessionStorage.setItem('finalTimestamp', finalTimestamp.toString())
      startTimer(finalTimestamp)
    }

    return () => {
      clearInterval(timerRef.current)
    }
  }, [initialTime, onComplete])

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`
  }

  const calculateProgress = (seconds: number) => {
    return ((initialTime - seconds) / initialTime) * 100
  }

  return (
    <div className="countdown-timer">
      <div className="countdown-timer__time-display">{formatTime(timeLeft)}</div>
      <div className="countdown-timer__progress-bar">
        <div
          className="countdown-timer__progress"
          style={
            {
              '--countdown-timer-progress': `${calculateProgress(timeLeft)}%`,
            } as CSSProperties
          }
        />
      </div>
      <div className="countdown-timer__message">
        {isMobile ? (
          <>
            {trans.timerLine?.line1 && trans.timerLine.line1}
            {trans.timerLine?.line2 && (
              <>
                <br />
                {trans.timerLine.line2}
              </>
            )}
          </>
        ) : (
          <>{trans.timerLine1}</>
        )}

        {trans.timerLine2 && (
          <>
            <br />
            {trans.timerLine2}
          </>
        )}
      </div>
    </div>
  )
}

export default CountdownTimer
